/** @jsxImportSource @emotion/react */

import { Player } from 'modules/players/domain/interfaces/player';
import { UDButton, UDListItem, UDText } from 'ud-ui/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import CategoryListPage from 'ud-ui/components/category/pages/category-list-page';
import { getBirthDay, getFullName } from 'shared/utils';

import { AppDispatch } from 'store/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { deleteTournamentUser } from '../../store/actions';
import usePlayerList from '../hooks/use-player-list';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import Modal from 'ud-ui/components/modal';

const naming = {
  searchPlaceholder: 'Поиск по игрокам',
  addButtonText: 'Добавить игрока',
};

function PlayerListPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { players, isLoading, loadNextPages, changeFilter, playersByFilter: filteringPlayer } = usePlayerList();
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // const { defaultOpen: openDelete, close: closeDelete, Modal: ModalDelete } = useModal();
  const { setHeaderContent } = useMainLayout();

  const [isOpen, setIsOpen] = useState(false);
  const openDelete = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeDelete = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCreateClick = useCallback(() => {
    navigate(Pages.PLAYERS.CREATE);
  }, [navigate]);

  const onPlayerClick = useCallback(
    (player: Player) => {
      navigate(Pages.PLAYERS.builders.view(player.id));
    },
    [navigate],
  );

  const handleOpenDeleteModal = useCallback(
    (player: Player) => {
      setSelectedPlayer(player);
      openDelete();
    },
    [openDelete],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setSelectedPlayer(null);
    closeDelete();
  }, [closeDelete]);

  const handleDeleteUser = useCallback(async () => {
    if (!selectedPlayer) {
      return;
    }
    setIsProcessing(true);
    await dispatch(deleteTournamentUser({ userId: selectedPlayer.id }))
      .unwrap()
      .then(() => {
        toast.success('Игрок успешно удалён');
        setSelectedPlayer(null);
        closeDelete();
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [closeDelete, dispatch, selectedPlayer]);

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Игроки" createButtonText="Добавить игрока" createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent]);

  const listItems = useMemo(() => {
    return (filteringPlayer || players).map((player, index) => {
      return (
        <UDListItem
          key={player.id}
          className="white mb-2"
          index={index + 1}
          image={player.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
          title={getFullName(player)}
          onClick={() => onPlayerClick(player)}
          subtitle={
            <div className="d-flex justify-content-between pl-4">
              <p
                style={{
                  width: '60%',
                  textAlign: 'center',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {getBirthDay({
                  birthDate: player.birthDate ? new Date(player.birthDate) : undefined,
                })}
              </p>
              <p
                style={{
                  width: '20%',
                  textAlign: 'center',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {player.height ? `${player.height} см` : '-'}
              </p>
              <p
                style={{
                  width: '20%',
                  textAlign: 'center',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {player.weight ? `${player.weight} кг` : '-'}
              </p>
            </div>
          }
          actions={[
            {
              label: 'Удалить',
              onClick: () => handleOpenDeleteModal(player),
              variant: 'secondary',
              style: { width: 144 },
            },
          ]}
        />
      );
    });
  }, [filteringPlayer, handleOpenDeleteModal, onPlayerClick, players]);

  return (
    <>
      <CategoryListPage
        categoryName="players"
        text={naming}
        isLoading={isLoading}
        getNextPages={loadNextPages}
        getFiltered={changeFilter}
        onCreateClick={handleCreateClick}
      >
        {listItems}
      </CategoryListPage>

      <Modal show={isOpen} onClose={closeDelete}>
        <Modal.Body className="flex-center flex-column gap-2">
          <UDText type="body-text">Удалить игрока?</UDText>
          <UDText type="subhead">
            Игрок <strong>{selectedPlayer ? getFullName(selectedPlayer) : undefined}</strong> будет безвозвратно удалён
          </UDText>
        </Modal.Body>

        <Modal.Footer>
          <UDButton variant="secondary" className="w-50" onClick={handleCloseDeleteModal} disabled={isProcessing}>
            Отмена
          </UDButton>
          <UDButton variant="primary" className="w-50" onClick={handleDeleteUser} loading={isProcessing}>
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PlayerListPage;
